import { useState, useEffect } from "react";
import Translate from "../../../utils/Translate";
import { useApi } from "../../../utils/Api";
import { useSnackbar } from "notistack";
import Preloader from "../../Preloader";
import { Button, TextField } from "@mui/material";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { useNavigate, useLocation } from "react-router-dom";
import InfoIcon from "@mui/icons-material/Info";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import MenuItem from "@mui/material/MenuItem";
import { DatePicker } from "@mui/x-date-pickers";
import { getCurrentDateMask, getCurrentInputFormat } from "../../../utils/dateTimeFormat";
import SelectActorPermitResponsibleActor from "../../actor-permits/SelectActorPermitResponsibleActor";
import { validateResponsibleActor } from "../../actor-permits/validation/ActorValidator";
import AutocompleteSearchField from "../../AutocompleteSearchField";
import ActorAutocompleteOption from "../../ActorAutocompleteOption";
import { actorCompanyTypes, actorSearch } from "../../../utils/actorSearch";
import CompanyCrudDialog from "../crud/CompanyCrudDialog";
import { accessKeys, hasSomeAccess } from "../../../utils/userAccess";

let fileExtension = "";
const acceptfileExtensions =
  ".txt,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp,.pages,.numbers,.key,.csv,.bmp,.gif,.heic,.jpg,.jpeg,.png,.tiff,.webp,.zip,.7z";

const UploadActorPermit = (props) => {
  const [fileName, setFileName] = useState("");
  const [skillGroupId, setSkillGroupId] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [responsibleActor, setResponsibleActor] = useState(null);
  const [company, setCompany] = useState(null);
  const [showCompanyCrud, setShowCompanyCrud] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const api = useApi();
  const location = useLocation();
  const navigate = useNavigate();
  const closeAndReload = () => {
    //Soft reload with redirect
    const loc = encodeURIComponent(location.pathname);
    navigate(`/redirect/${loc}`, { replace: true });
  };
  const [skillGroups, setSkillGroups] = useState(false);
  if (!skillGroups) {
    (async () => {
      const data = await api.fetch(
        `${import.meta.env.VITE_MAIN_URL}skillgroups/for-permits?includeImageUrls=true`,
        false,
        "GET",
      );
      setSkillGroups(data);
    })();
  }

  const [errors, setErrors] = useState({});

  useEffect(() => {
    async function loadParentActor() {
      if (!props.actorData) {
        return;
      }

      if (props.actorData.firstParentActorId == null) {
        return;
      }

      // Hoppa över om parentActor är "ePortMaster" (actorId = 4) eftersom det inte är ett riktigt företag
      if (props.actorData.firstParentActorId === 4) {
        return;
      }

      const result = await actorSearch(props.actorData.firstParentActorId, actorCompanyTypes, ["ActorId"], 1, true);
      const actor = Array.isArray(result) ? result[0] : result;
      if (!actor) {
        return;
      }

      const mapped = {
        actorId: actor.actorId,
        actorName: actor.actorName,
        orgNo: actor.orgNo,
        externalActorNo: actor.externalActorNo,
        email: actor.email,
        profilePictureUrl: actor.profilePictureUrl,
        phoneNumber: actor.phoneNumbers?.find((ph) => ph.type === 2)?.phoneNumber,
        companies:
          actor.parentActors
            ?.filter((x, index, all) => x.parentActor?.actorId > 0)
            .map((x) => ({ actorId: x.parentActor.actorId, actorName: x.parentActor.actorName })) ?? [],
      };

      setCompany(mapped);
    }

    loadParentActor();
  }, [props.actorData]);

  function handleChangeResponsibleActor(actor) {
    if (actor) {
      const validationError = validateResponsibleActor(actor);
      setErrors({ ...errors, responsibleActor: validationError });
    }
    setResponsibleActor(actor);
  }

  const submitForm = (e) => {
    e.preventDefault();
    let validFrom = fromDate.toJSON();
    let validTo = toDate ? toDate.toJSON() : null;
    setSubmitting(true);
    const formData = new FormData();
    formData.append("file", e.target.file.files[0], fileName + fileExtension);
    formData.append("skillGroupId", skillGroupId);
    formData.append("validFrom", validFrom);
    validTo && formData.append("validTo", validTo);
    formData.append("actorId", props.actorData.actorId);
    formData.append("actorName", props.actorData.actorName || "");
    formData.append("actorEmail", props.actorData.actorEmail || "");
    formData.append("actorOrgno", props.actorData.actorOrgNo || "");
    props.actorData.externalActorNo && formData.append("externalActorNo", props.actorData.externalActorNo || "");
    formData.append("responsibleActorId", responsibleActor.actorId);
    formData.append("responsibleActorName", responsibleActor.actorName || "");
    formData.append("responsibleActorEmail", responsibleActor.email || "");
    formData.append("companyActorId", company?.actorId || "");

    (async () => {
      const ret = await api.fetch(`${import.meta.env.VITE_MAIN_URL}permits/from/file`, formData);
      if (ret.isSuccessful) {
        setSubmitting(false);
        enqueueSnackbar(Translate.get("DocumentUploaded"), {
          variant: "success",
          autoHideDuration: 3000,
        });
        closeAndReload();
      } else {
        setSubmitting(false);
      }
    })();
  };
  const showErrorMessage = (errorMessageTranslationKey) => {
    const errorMessage = enqueueSnackbar(Translate.get(errorMessageTranslationKey), {
      variant: "error",
      autoHideDuration: 6000,
      onClick: () => closeSnackbar(errorMessage),
    });
  };
  const getFile = (e) => {
    const maxFileSize = 1024 * 1024 * 5;
    const file = e.target.files[0];
    if (file.size > maxFileSize) {
      showErrorMessage("FileIsTooLarge");
      setFileName("");
    } else {
      const lastDot = file.name.lastIndexOf(".");

      const fileNameNoExt = file.name.substring(0, lastDot);
      fileExtension = file.name.substring(lastDot);
      const chkValidExt = acceptfileExtensions.split(",");
      if (chkValidExt.includes(fileExtension)) {
        setFileName(fileNameNoExt);
      } else {
        showErrorMessage("FileTypeNotSupported");
      }
    }
  };

  async function CompanySearch(searchText) {
    const foundActors = await actorSearch(searchText, actorCompanyTypes, ["ActorName", "OrgNo", "ExternalActorNo"], 50);
    if (foundActors) {
      return foundActors.map((actor) => ({
        actorId: actor.actorId,
        actorName: actor.actorName,
        orgNo: actor.orgNo,
        externalActorNo: actor.externalActorNo,
        email: actor.email,
        profilePictureUrl: actor.profilePictureUrl,
        phoneNumber: actor.phoneNumbers?.find((ph) => ph.type === 2)?.phoneNumber,
        companies: actor.parentActors
          ?.filter(
            (x, index, allParentActors) =>
              x.parentActor.actorId !== actor.actorId &&
              x.parentActor.actorId > 0 &&
              allParentActors.findIndex((y) => y.parentActor.actorId === x.parentActor.actorId) === index,
          )
          .map((x) => ({
            actorId: x.parentActor.actorId,
            actorName: x.parentActor.actorName,
          })),
      }));
    }
  }

  function handleCompanyChange(company) {
    if (!company) {
      // This happens if the user presses the "Enter" key,
      // rather than selecting the "Add" from the list
      setCompany(null);
      return;
    }

    const isExistingActor = company.actorId > 0;
    if (!isExistingActor) {
      setShowCompanyCrud(true);
    }

    setCompany(company);
  }

  function handleCancelAddCompany() {
    setCompany(null);
    setShowCompanyCrud(false);
  }

  //Todo lista alla uppladdade dokument och delete funktion + förbered för fler kopplingar
  if (skillGroups === false) {
    return <Preloader />;
  }
  return (
    <>
      {showCompanyCrud && (
        <CompanyCrudDialog
          open={showCompanyCrud}
          companyCrudProps={{
            name: company.actorName ?? "",
            onClose: () => setShowCompanyCrud(false),
            onActorSaved: (actor) => setCompany(actor),
            actorId: company?.actorId,
            //parentActorId: drivers[driverToCrudIndex]?.companies?.[0]?.actorId ?? newActorParentActorId,
            //actorRole: "permit-holder",
            onCancel: () => handleCancelAddCompany(),
          }}
        />
      )}

      {skillGroups && (
        <>
          <DialogTitle>
            <div className="stdFlexBetween">
              <div>{Translate.get("UploadActorPermit")}</div>
              {/*  <Tooltip title={Translate.get('ePort2AllowedFileTypesInfo') + ' ' + acceptfileExtensions}>
                <IconButton>
                  <InfoIcon color="primary" />
                </IconButton>
              </Tooltip> */}
            </div>
          </DialogTitle>
          <form onSubmit={submitForm}>
            <DialogContent dividers={true} className="stdDialogContent">
              <div className="stdForm">
                <div className="stdFlexBetween">
                  <div className="smallHeader">{Translate.get("FileUpload")}</div>
                  <Tooltip title={Translate.get("ePort2AllowedFileTypesInfo") + " " + acceptfileExtensions}>
                    <IconButton>
                      <InfoIcon color="primary" />
                    </IconButton>
                  </Tooltip>
                </div>
                <div>{Translate.get("ePort2MaxFileSize")}</div>

                {/*  <UploadList files={files} /> */}
                <div className="stdFlexBetween">
                  <Button variant="contained" component="label">
                    {Translate.get("SelectFile")}
                    <input accept={acceptfileExtensions} onChange={getFile} type="file" hidden name="file" />
                  </Button>
                  <TextField
                    style={{ flexGrow: 1 }}
                    type="text"
                    required
                    name="name"
                    label={Translate.get("FileName")}
                    value={fileName}
                    onChange={(event) => {
                      setFileName(event.target.value);
                    }}
                  />
                </div>
                <div className="stdFlexBetween">
                  <div className="smallHeader">{Translate.get("OtherDetails")}</div>
                  {/*  <Tooltip title={Translate.get('ePort2AllowedFileTypesInfo') + ' ' + acceptfileExtensions}>
                    <IconButton>
                      <InfoIcon color="primary" />
                    </IconButton>
                  </Tooltip> */}
                </div>
                <div className="stdFlexBetween">
                  <TextField
                    select
                    onChange={(e) => setSkillGroupId(e.target.value)}
                    value={skillGroupId}
                    label={Translate.get("MachineGroup")}
                    defaultValue={skillGroups[0].skillGroupId}
                    name="skillGroupId"
                    fullWidth
                  >
                    {skillGroups.map((row) => (
                      <MenuItem key={row.skillGroupId} value={row.skillGroupId}>
                        {row.description}
                      </MenuItem>
                    ))}
                  </TextField>
                </div>
                <div className="stdFlexBetween">
                  <DatePicker
                    displayWeekNumber={hasSomeAccess(accessKeys.swedishCompany, accessKeys.isMASystem)}
                    sx={{ flexGrow: 1 }}
                    label={Translate.get("ValidFrom")}
                    mask={getCurrentDateMask()}
                    format={getCurrentInputFormat()}
                    value={fromDate}
                    onChange={(newValue) => {
                      setFromDate(newValue);
                    }}
                    slotProps={{
                      textField: {
                        required: true,
                        name: "validFrom",
                        size: "small",
                      },
                    }}
                  />

                  <DatePicker
                    displayWeekNumber={hasSomeAccess(accessKeys.swedishCompany, accessKeys.isMASystem)}
                    sx={{ flexGrow: 1 }}
                    //disableOpenPicker={true}
                    //fullWidth
                    label={Translate.get("ValidTo")}
                    mask={getCurrentDateMask()}
                    minDate={fromDate}
                    format={getCurrentInputFormat()}
                    value={toDate}
                    onChange={(newValue) => {
                      setToDate(newValue);
                    }}
                    slotProps={{ textField: { name: "validTo", size: "small" } }}
                  />
                </div>
                <div className="stdFlexRight">
                  <div className="smallInfoText">{Translate.get("NoEndDateValid")}</div>
                </div>
                <div style={{ display: "block" }}>
                  <SelectActorPermitResponsibleActor
                    responsibleActor={responsibleActor}
                    onResponsibleActorChange={handleChangeResponsibleActor}
                    errorMessage={errors.responsibleActor}
                  />
                </div>

                <div className="smallHeader">{Translate.get("Company")}</div>
                <AutocompleteSearchField
                  label={Translate.get("Company") + " *"}
                  value={company}
                  onValueChange={handleCompanyChange}
                  getOptionLabel={(option) => option?.actorName ?? ""}
                  renderOption={(option) => (
                    <ActorAutocompleteOption
                      actorName={option?.actorName}
                      orgNo={option?.orgNo}
                      email={option?.email}
                      companies={option?.companies?.map((x) => x.actorName)}
                      parentActors={option?.parentActors}
                      externalActorNo={option?.externalActorNo}
                    />
                  )}
                  keyPropName="actorId"
                  requireSelection={true}
                  search={CompanySearch}
                  createNewValue={(text) => ({
                    actorName: text,
                  })}
                />
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => closeAndReload()}>{Translate.get("Close")}</Button>
              <Button
                className="stdLightLoader"
                loading={submitting}
                disabled={!fileName || !skillGroupId || !fromDate || !responsibleActor || !company}
                type="submit"
              >
                {Translate.get("Save")}
              </Button>
            </DialogActions>
          </form>
        </>
      )}
    </>
  );
};
export default UploadActorPermit;
